import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { onPermissionDenied } from "../api/permissionEvents";
import { createPermissionChecker, legacyFallbackPermissions } from "../utils/permission";
import { useUser } from "./UserContext";

/**
 * 어드민 권한 컨텍스트 (spec R-FE-01, R-FE-02).
 * GET /admin/access/me 결과를 보관하고 hasPermission(key) 하나로 메뉴·버튼 노출을 판단한다. 판단의 정본은 API다.
 * 재조회 시점: 로그인 성공 직후(Login·AutoLogin이 refreshAccess 호출) / window focus / API 403 permission/denied.
 * fetchMe(= api/access.js fetchMyAccess)는 App이 주입한다(이 모듈이 axios를 직접 불러오지 않게 — 테스트에서 axios ESM 파싱 회피).
 *
 * source
 *  - "api": /me 성공
 *  - "legacy-fallback": /me가 한 번도 성공하지 못했고 실패함(API 미배포 등) → legacy role 대체표 (utils/permission.js)
 *  - null: 로그인 전
 */
export const EMPTY_ACCESS = Object.freeze({
  status: "idle",
  source: null,
  adminUserId: null,
  roles: [],
  permissions: [],
  mode: null,
});

/** /me 조회 결과로 다음 상태를 만든다. 실패 시: 이미 API 결과가 있으면 유지, 없으면 legacy 대체표. */
export async function loadAccessState(fetchMe, userInfo, prev = EMPTY_ACCESS) {
  try {
    const me = await fetchMe();
    if (!me || !Array.isArray(me.permissions)) throw new Error("unexpected /me response");
    return {
      status: "ready",
      source: "api",
      adminUserId: me.adminUserId ?? null,
      roles: Array.isArray(me.roles) ? me.roles : [],
      permissions: me.permissions,
      mode: me.mode ?? null,
    };
  } catch (error) {
    if (prev.source === "api") return prev; // 일시 장애로 메뉴가 바뀌지 않게 직전 결과 유지
    if (!userInfo) return EMPTY_ACCESS;
    console.warn(
      "권한 조회(/admin/access/me) 실패 — legacy role 기준 대체 권한으로 표시합니다.",
      error?.response?.status ?? error?.message,
    );
    return {
      status: "ready",
      source: "legacy-fallback",
      adminUserId: userInfo.adminUserId ?? userInfo.userId ?? null, // 로그인 응답은 userId 로 준다
      roles: [],
      permissions: legacyFallbackPermissions(userInfo.role),
      mode: null,
    };
  }
}

const withChecker = (state) => ({ ...state, hasPermission: createPermissionChecker(state.permissions) });

const defaultValue = {
  ...withChecker(EMPTY_ACCESS),
  refreshAccess: async () => withChecker(EMPTY_ACCESS),
};

export const AccessContext = createContext(defaultValue);

export function AccessProvider({ fetchMe, children }) {
  const { userInfo } = useUser();
  const [state, setState] = useState(EMPTY_ACCESS);
  const stateRef = useRef(EMPTY_ACCESS);
  const userRef = useRef(userInfo);
  const inflightRef = useRef(null);

  useEffect(() => {
    userRef.current = userInfo;
    if (!userInfo) {
      // 로그아웃 → 권한 비움
      stateRef.current = EMPTY_ACCESS;
      setState(EMPTY_ACCESS);
    }
  }, [userInfo]);

  /** /me 재조회. 진행 중인 조회가 있으면 그 결과를 함께 기다린다. userInfoOverride: 로그인 직후 setUserInfo 반영 전 값 */
  const refreshAccess = useCallback((userInfoOverride) => {
    if (!inflightRef.current) {
      const user = userInfoOverride ?? userRef.current;
      inflightRef.current = loadAccessState(fetchMe, user, stateRef.current)
        .then((next) => {
          stateRef.current = next;
          setState(next);
          return withChecker(next);
        })
        .finally(() => {
          inflightRef.current = null;
        });
    }
    return inflightRef.current;
  }, [fetchMe]);

  // window focus → 재조회 (다른 관리자가 권한을 바꾼 경우 반영)
  useEffect(() => {
    const handleFocus = () => {
      if (userRef.current) refreshAccess();
    };
    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [refreshAccess]);

  // API 403 /errors/permission/denied → 재조회
  useEffect(
    () =>
      onPermissionDenied(() => {
        if (userRef.current) refreshAccess();
      }),
    [refreshAccess],
  );

  const value = useMemo(() => ({ ...withChecker(state), refreshAccess }), [state, refreshAccess]);

  return <AccessContext.Provider value={value}>{children}</AccessContext.Provider>;
}

/** { hasPermission(key), permissions, roles, mode, source, refreshAccess } */
export const useAccess = () => useContext(AccessContext);

/** 테스트·고정 권한용 Provider (API 호출 없음) */
export function StaticAccessProvider({ permissions = [], children }) {
  const value = useMemo(
    () => ({
      ...withChecker({ ...EMPTY_ACCESS, status: "ready", source: "api", permissions }),
      refreshAccess: async () => withChecker({ ...EMPTY_ACCESS, status: "ready", source: "api", permissions }),
    }),
    [permissions],
  );
  return <AccessContext.Provider value={value}>{children}</AccessContext.Provider>;
}

/** 권한이 있을 때만 children을 렌더한다(없으면 DOM에 남기지 않음 — R-FE-04). */
export function IfPermitted({ requires, children }) {
  const { hasPermission } = useAccess();
  return hasPermission(requires) ? children : null;
}
